/**
 * Which groups one account belongs to, within one organization.
 *
 * The mirror image of the Groups card on the organization page: that one
 * picks users for a group, this one picks groups for a user. Groups are
 * organization-scoped, so an organization picker comes first.
 *
 * Saving replaces the whole set — the platform computes the additions and
 * removals — and an empty selection revokes the account's access to that
 * organization's ingested data.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { ApiError } from '@optum/api-client/client';
import { listGroups, listMemberGroups, setMemberGroups } from '@optum/api-client/endpoints/groups';
import type { AccountMembership } from '@optum/api-client/types';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { DividedList } from '../../ui/DividedList.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { Checkbox } from '../../ui/Checkbox.js';
import { Select } from '../../ui/Select.js';

export function MemberGroupsEditor({
  platformUserId,
  memberships,
}: {
  platformUserId: string;
  memberships: AccountMembership[];
}) {
  const activeMemberships = useMemo(
    () => memberships.filter((m) => m.status === 'active'),
    [memberships],
  );
  const [orgId, setOrgId] = useState<string>('');

  useEffect(() => {
    if (orgId === '' && activeMemberships.length > 0) {
      setOrgId(activeMemberships[0]!.organization_id);
    } else if (orgId !== '' && !activeMemberships.some((m) => m.organization_id === orgId)) {
      setOrgId(activeMemberships[0]?.organization_id ?? '');
    }
  }, [activeMemberships, orgId]);

  if (activeMemberships.length === 0) {
    return (
      <EmptyBlock>This account has no active membership, so it can hold no groups.</EmptyBlock>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
          Organization
        </span>
        <Select
          searchable
          value={orgId}
          onChange={(value) => setOrgId(value)}
          options={[
            { value: '', label: 'Select an organization' },
            ...activeMemberships.map((m) => ({
              value: m.organization_id,
              label: m.organization_name,
            })),
          ]}
        />
      </label>
      {orgId !== '' && <GroupSelection key={orgId} orgId={orgId} platformUserId={platformUserId} />}
    </div>
  );
}

/**
 * One organization's groups as a checklist for one person, saved as a whole
 * set. Used here under the organization picker, and on its own where the
 * organization is already known (the organization's Users panel).
 */
export function GroupSelection({
  orgId,
  platformUserId,
}: {
  orgId: string;
  platformUserId: string;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const all = useQuery({
    queryKey: qk.groups(orgId),
    queryFn: () => listGroups(orgId),
  });
  const mine = useQuery({
    queryKey: qk.memberGroups(orgId, platformUserId),
    queryFn: () => listMemberGroups(orgId, platformUserId),
  });
  const [selected, setSelected] = useState<string[] | null>(null);

  // Seed the form once the server state arrives; null means "not yet edited".
  const current = useMemo(() => (mine.data ?? []).map((g) => g.id).sort(), [mine.data]);
  const value = selected ?? current;
  const dirty = selected !== null && [...selected].sort().join() !== current.join();

  const save = useMutation({
    mutationFn: () => setMemberGroups(orgId, platformUserId, value),
    onSuccess: () => {
      toast.success('Groups updated');
      setSelected(null);
      qc.invalidateQueries({ queryKey: qk.memberGroups(orgId, platformUserId) });
      // member_count lives on the group record.
      qc.invalidateQueries({ queryKey: qk.groups(orgId) });
    },
    onError: (e) =>
      toast.error('Could not update groups', e instanceof ApiError ? e.message : String(e)),
  });

  if (all.isPending || mine.isPending) return <LoadingBlock />;
  if (all.isError || mine.isError) {
    const err = all.error ?? mine.error;
    return <ErrorBlock message={err instanceof Error ? err.message : 'Failed to load'} />;
  }
  if (all.data.length === 0) {
    return <EmptyBlock>This organization has no groups yet.</EmptyBlock>;
  }

  const toggle = (id: string, on: boolean) =>
    setSelected(on ? [...value, id] : value.filter((g) => g !== id));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <DividedList
        as="div"
        style={{
          maxHeight: 256,
          overflowY: 'auto',
          borderRadius: 'var(--radius-chip)',
          border: '1px solid var(--border-hairline)',
        }}
      >
        {all.data.map((g) => (
          <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: 11 }}>
            <Checkbox
              checked={value.includes(g.id)}
              onChange={(checked) => toggle(g.id, checked)}
              disabled={save.isPending}
            />
            <div style={{ flex: '1 1 0%' }}>
              <div
                style={{
                  fontSize: 'var(--text-body-md)',
                  fontWeight: 'var(--weight-medium)',
                  color: 'var(--text-strong)',
                }}
              >
                {g.name}
              </div>
              {g.description && (
                <div style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
                  {g.description}
                </div>
              )}
            </div>
          </label>
        ))}
      </DividedList>
      {/* Unticking every group is a loss worth a warning; already being in none is a state. */}
      {value.length === 0 && dirty && (
        <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--danger)' }}>
          No groups selected. Saving removes this account&apos;s access to every document ingested
          for this organization.
        </p>
      )}
      {value.length === 0 && !dirty && (
        <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--warn)' }}>
          In no group: reads no documents here until added to one.
        </p>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <Button
          variant="primary"
          type="button"
          size="sm"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save groups'}
        </Button>
        {dirty && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={save.isPending}
            onClick={() => setSelected(null)}
          >
            Reset
          </Button>
        )}
      </div>
    </div>
  );
}
